import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "wouter";

function isMissingQueryParam(value: string | null | undefined): boolean {
    return value === null || value === undefined || value === "null" || value === "";
}

export function useQueryState(key: string, defaultValue: string): [string, (newValue: string) => void] {
    const [queryArgs, setQueryArgs] = useSearchParams();

    const paramValue = queryArgs.get(key);
    const [value, setValueState] = useState<string>(isMissingQueryParam(paramValue) ? defaultValue : paramValue);

    useEffect(() => {
        const paramValue = queryArgs.get(key);
        if (!isMissingQueryParam(paramValue) && paramValue !== value) {
            setValueState(paramValue);
        }

        if (isMissingQueryParam(paramValue)) {
            if (queryArgs.has(key) && (defaultValue === null || defaultValue === "")) {
                setQueryArgs(
                    (prev) => {
                        prev.delete(key);
                        return prev;
                    },
                    { replace: true }
                );
            } else {
                if (defaultValue !== null && defaultValue !== "") {
                    setQueryArgs(
                        (prev) => {
                            prev.set(key, defaultValue);
                            return prev;
                        },
                        { replace: true }
                    );
                }
            }
        }
    }, [defaultValue, key, queryArgs, setQueryArgs, value]);

    const setValue = useCallback(
        (newValue: string) => {
            setQueryArgs((prev) => {
                if (prev.get(key) !== newValue) {
                    prev.set(key, newValue);
                }
                return prev;
            });
            setValueState(newValue);
        },
        [key, setQueryArgs]
    );

    return [value, setValue];
}

/**
 * Like useQueryState but does NOT write default to URL when param is missing.
 * Use when you want a clean URL until the user makes explicit choices.
 */
export function useQueryStateLazy(key: string, defaultValue: string): [string, (newValue: string) => void] {
    const [queryArgs, setQueryArgs] = useSearchParams();
    const paramValue = queryArgs.get(key);
    const [value, setValueState] = useState<string>(isMissingQueryParam(paramValue) ? defaultValue : paramValue);

    useEffect(() => {
        const p = queryArgs.get(key);
        if (!isMissingQueryParam(p) && p !== value) {
            setValueState(p);
        }
    }, [key, queryArgs, value]);

    const setValue = useCallback(
        (newValue: string) => {
            setQueryArgs((prev) => {
                if (prev.get(key) !== newValue) {
                    prev.set(key, newValue);
                }
                return prev;
            });
            setValueState(newValue);
        },
        [key, setQueryArgs]
    );

    return [value, setValue];
}

export function useQueryStateArray(key: string, defaultValue: string[]): [string[], (newValue: string[]) => void] {
    const [queryArgs, setQueryArgs] = useSearchParams();

    const [value, setValueState] = useState<any[]>(queryArgs.getAll(key) || defaultValue);

    useEffect(() => {
        const paramValue = queryArgs.getAll(key);
        if (defaultValue && defaultValue.length > 0 && paramValue.length === 0) {
            setQueryArgs((prev) => {
                defaultValue.forEach((v) => {
                    prev.append(key, v);
                });
                return prev;
            });
        }
    }, [defaultValue, key, queryArgs, setQueryArgs]);

    const setValue = useCallback(
        (newValue: string[]) => {
            setQueryArgs((prev) => {
                prev.delete(key);
                newValue.forEach((v) => {
                    prev.append(key, v);
                });
                return prev;
            });
            setValueState(newValue);
        },
        [key, setQueryArgs]
    );

    return [value, setValue];
}
